// src/pages/join.tsx

import React from 'react';
import { Container, Typography, Box, Grid, Card, CardContent, useMediaQuery, alpha, GlobalStyles } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import JoinForm from '../components/JoinForm';
import MetaTagManager, { createJoinPageMetaData } from '@/components/MetaTagManager';

// Import assets
import CrowdImage from '../assets/crowd.jpg';

const JoinPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <MetaTagManager data={createJoinPageMetaData()}>
      {/* Add global styles for font */}
      <GlobalStyles
        styles={{
          '@import': 'url("https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=swap")',
          'body, .MuiTypography-root, .MuiButton-root, .MuiInputBase-root': {
            fontFamily: '"Poppins", sans-serif !important'
          },
          'input, select, textarea, label, .MuiInputLabel-root, .MuiMenuItem-root, .MuiChip-label': {
            fontFamily: '"Poppins", sans-serif !important'
          }
        }}
      />

      <Box
        sx={{
          background: 'linear-gradient(135deg, #FF0054, #662d91)',
          minHeight: '100vh',
          color: '#fff',
          py: { xs: 4, md: 0 },
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {/* Background gradient overlay with subtle animation */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(ellipse at bottom right, rgba(255,0,84,0.3), transparent 70%)',
            animation: 'pulse 8s infinite ease-in-out',
            '@keyframes pulse': {
              '0%': { opacity: 0.7 },
              '50%': { opacity: 0.9 },
              '100%': { opacity: 0.7 },
            },
            zIndex: 0,
          }}
        />

        {/* Main content container */}
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, height: '100%', py: { md: 4 } }}>
          <Grid 
            container 
            spacing={4} 
            sx={{ 
              minHeight: { md: 'calc(100vh - 64px)' },
              alignItems: { xs: 'center', md: 'flex-start' },
              pt: { md: 6 }
            }}
          >
            {/* Left column - Image and marketing copy */}
            <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column', mb: { xs: 4, md: 0 } }}>
              {/* Main Title at the top */}
              <Typography variant="h2" sx={{
                fontWeight: 700,
                lineHeight: 1.1,
                letterSpacing: '-0.02em',
                fontSize: { xs: '1.8rem', md: '2.5rem' },
                color: '#fff',
                textShadow: '0 2px 8px rgba(255,0,84,0.6)',
                mb: 3
              }}>
                <Box component="span" sx={{ fontWeight: 900, fontSize: { xs: '2rem', md: '2.8rem' }, mr: 0.5 }}>
                  Lsnto
                </Box>
                : The Artist-First Music Platform
              </Typography>

              {/* Hero Image */}
              <Box 
                component="div" 
                sx={{ 
                  position: 'relative',
                  width: '100%',
                  height: { xs: '200px', sm: '280px', md: '320px' },
                  borderRadius: '16px',
                  overflow: 'hidden',
                  boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
                  mb: 4,
                }}
              >
                <img 
                  src={CrowdImage} 
                  alt="Concert crowd with raised hands" 
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    padding: 3,
                    background: 'linear-gradient(to top, rgba(0,0,0,0.9), transparent)',
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="h6" fontWeight="600" sx={{ color: '#fff', fontSize: { xs: '1rem', md: '1.1rem' } }}>
                    A platform built for musicians who care about their craft — not chasing metrics.
                  </Typography>
                </Box>
              </Box>
              {/* Mission Statement Box */}
              <Box sx={{ 
                mb: 4, 
                p: 3, 
                borderRadius: '12px', 
                background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                border: '1px solid rgba(255,255,255,0.2)',
                backdropFilter: 'blur(10px)',
                position: 'relative',
              }}>
                <Typography variant="h5" sx={{ 
                  fontWeight: 700, 
                  mb: 2, 
                  color: '#fff',
                  fontSize: { xs: '1.2rem', md: '1.4rem' }
                }}>
                  We're not a tech company in music.<br />
                  <Box component="span" sx={{ color: '#E4FF1A' }}>We're a music company fixing the tech.</Box>
                </Typography>
                <Typography variant="body1" sx={{ 
                  lineHeight: 1.6, 
                  fontSize: '0.95rem',
                  color: 'rgba(255,255,255,0.9)'
                }}>
                  Lsnto is a music platform built by artists, not tech giants! It has been designed to put creators back in control. Our goal is simple but seemingly radical: we want to reintroduce value to digital music by combining fair pay, fan-powered discovery, and innovative release tools.
                </Typography>
              </Box>

              {/* The Problem Section */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h4" sx={{ 
                  fontWeight: 800, 
                  mb: 3, 
                  color: '#fff',
                  fontSize: { xs: '1.4rem', md: '1.8rem' },
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -8,
                    left: 0,
                    width: '60px',
                    height: '4px',
                    background: '#E4FF1A',
                    borderRadius: '2px',
                  }
                }}>
                  The Problem
                </Typography>
                <Grid container spacing={2}>
                  {[
                    'Streaming platforms were built to serve shareholders, not artists.',
                    'You\'ve been underpaid, undervalued, and unheard.',
                    'The current streaming model is only marginally better than music piracy and hasn\'t evolved in 20 years!',
                    'Fake streams, algorithmic suppression, AI bands and zero control over your audience.'
                  ].map((problem, index) => (
                    <Grid item xs={12} sm={6} key={index}>
                      <Box sx={{ 
                        p: 2, 
                        borderRadius: '8px',
                        background: 'rgba(255,0,84,0.1)',
                        border: '1px solid rgba(255,0,84,0.3)',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                        <Typography variant="body2" sx={{ 
                          fontSize: '0.85rem',
                          color: 'rgba(255,255,255,0.9)',
                          lineHeight: 1.4
                        }}>
                          • {problem}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>

              {/* Solution Section */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h3" sx={{ 
                  fontWeight: 800, 
                  mb: 2, 
                  fontSize: { xs: '1.6rem', md: '2rem' },
                  background: 'linear-gradient(90deg, #fff 70%, rgba(255,0,84,0.6) 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}>
                  Music Should Pay Artists
                </Typography>
                
                <Typography variant="h6" sx={{ 
                  mb: 3, 
                  fontWeight: 600,
                  color: '#fff',
                  fontSize: { xs: '1rem', md: '1.1rem' }
                }}>
                  Lsnto gives you back what big tech stole:<br />
                  <Box component="span" sx={{ 
                    color: '#E4FF1A',
                    fontWeight: 700,
                    textShadow: '0 0 10px rgba(228,255,26,0.3)'
                  }}>
                    Control. Fairness. Visibility. Community.
                  </Box>
                </Typography>
                
                <Grid container spacing={2}>
                  {[
                    '80% artist royalty rate on streaming',
                    'Opportunity to earn CD sales equivalent in Lsnto\'s new business models',
                    'No big label bias — all artists get equal footing',
                    'Fan-first release tools',
                    'Direct artist-to-fan engagement',
                    'Real fan insights, not just surface level metrics',
                    'Artist-first analytics & revenue reporting'
                  ].map((benefit, index) => (
                    <Grid item xs={12} sm={6} key={index}>
                      <Box sx={{ 
                        p: 2, 
                        borderRadius: '8px',
                        background: 'rgba(228,255,26,0.1)',
                        border: '1px solid rgba(228,255,26,0.3)',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                        <Typography variant="body2" sx={{ 
                          fontSize: '0.85rem',
                          color: 'rgba(255,255,255,0.9)',
                          lineHeight: 1.4
                        }}>
                          • {benefit}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>

              {/* Standout Quote */}
              <Box sx={{ 
                mb: 4, 
                p: 3, 
                borderRadius: '12px',
                background: 'linear-gradient(135deg, rgba(228,255,26,0.2) 0%, rgba(255,0,84,0.2) 100%)',
                border: '2px solid rgba(228,255,26,0.5)',
                textAlign: 'center',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)',
                  animation: 'shimmer 3s infinite',
                  '@keyframes shimmer': {
                    '0%': { left: '-100%' },
                    '100%': { left: '100%' }
                  }
                }
              }}>
                <Typography variant="h5" sx={{ 
                  fontWeight: 700, 
                  color: '#fff',
                  fontSize: { xs: '1.1rem', md: '1.3rem' },
                  textShadow: '0 2px 4px rgba(0,0,0,0.3)'
                }}>
                  The music is <Box component="span" sx={{ color: '#E4FF1A' }}>YOUR</Box> product, not the platform owners!!
                </Typography>
              </Box>

              {/* Artist Portal Section */}
              <Box sx={{ mb: 5 }}>
                <Box
                  component="img"
                  src="/artistportal.png"
                  alt="Lsnto Artist Portal"
                  sx={{
                    width: '100%',
                    borderRadius: '12px',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.4)',
                    mb: 3,
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 15px 50px rgba(228,255,26,0.2)',
                    }
                  }}
                />
                <Typography variant="h5" sx={{ 
                  fontWeight: 700, 
                  mb: 2, 
                  color: '#fff',
                  fontSize: { xs: '1.2rem', md: '1.4rem' }
                }}>
                  Take Control with the Artist Portal
                </Typography>
                <Typography variant="body1" sx={{ 
                  lineHeight: 1.6, 
                  fontSize: '0.95rem',
                  color: 'rgba(255,255,255,0.9)'
                }}>
                  Manage your presence on Lsnto with our dedicated{' '}
                  <Box
                    component="a"
                    href="https://artist.lsnto.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: '#E4FF1A',
                      textDecoration: 'none',
                      fontWeight: 600,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        textDecoration: 'underline',
                        textShadow: '0 0 10px rgba(228,255,26,0.5)'
                      }
                    }}
                  >
                    Artist Portal
                  </Box>
                  . View your analytics, schedule releases, update your profile and connect with your fans.
                </Typography>
              </Box>

              {/* Join the Movement */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h4" sx={{ 
                  fontWeight: 800, 
                  mb: 3, 
                  color: '#fff',
                  fontSize: { xs: '1.4rem', md: '1.8rem' },
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -8,
                    left: 0,
                    width: '80px',
                    height: '4px',
                    background: '#E4FF1A',
                    borderRadius: '2px',
                  }
                }}>
                  Join the Movement
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {[
                    'Free verified artist account, engagement and promotion tools',
                    'No exclusivity required. You\'re free to release where you want',
                    '80% revenue share with the opportunity to earn up to 10x compared to mainstream platforms',
                    'Featured in the "Lsnto First" Artist Spotlight with other early joiner perks',
                    'Private invite to Lsnto creator community'
                  ].map((benefit, index) => (
                    <Box key={index} sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 2,
                      p: 2,
                      borderRadius: '8px',
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        background: 'rgba(255,255,255,0.1)',
                        transform: 'translateX(5px)'
                      }
                    }}>
                      <Box sx={{ 
                        width: '8px', 
                        height: '8px', 
                        borderRadius: '50%', 
                        background: 'linear-gradient(90deg, #fff 70%, rgba(255,0,84,0.6) 100%)',
                        flexShrink: 0
                      }} />
                      <Typography variant="body2" sx={{ 
                        fontSize: '0.9rem',
                        color: 'rgba(255,255,255,0.9)',
                        lineHeight: 1.4
                      }}>
                        {benefit}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Final CTA Quote */}
              <Box sx={{ 
                p: 3, 
                borderRadius: '12px',
                background: 'linear-gradient(135deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.1) 100%)',
                border: '1px solid rgba(228,255,26,0.3)',
                textAlign: 'center',
                position: 'relative'
              }}>
                <Typography variant="h6" sx={{ 
                  fontWeight: 600, 
                  color: '#fff',
                  fontSize: { xs: '1.2rem', md: '1.5rem' },
                  lineHeight: 1.4,
                }}>
                  If you're tired of being just a data point, come build a real music movement with us.
                </Typography>
              </Box>
            </Grid>
            
            {/* Right column - Form */}
            <Grid item xs={12} md={6}>
              <Card 
                sx={{
                  maxWidth: { md: '90%', lg: '85%' },
                  borderRadius: '16px',
                  backgroundColor: alpha('#000', 0.5),
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                  overflow: 'visible',
                  ml: { md: 'auto' },
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: -10,
                    left: -10,
                    right: -10,
                    bottom: -10,
                    background: 'linear-gradient(45deg, #FF0054, transparent, #662d91)',
                    borderRadius: '20px',
                    zIndex: -1,
                    opacity: 0.6,
                  }
                }}
              >
                <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                    <img 
                      src="/logo2.png"
                      alt="Lsnto Logo" 
                      style={{
                        height: '60px',
                        width: 'auto',
                      }} 
                    />
                  </Box>
                  
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, textAlign: 'center' }}>
                    Join the Waitlist
                  </Typography>
                  
                  <Typography variant="body1" sx={{ mb: 4, opacity: 0.9, textAlign: 'center' }}>
                    Get early access and help shape the future of music streaming.
                  </Typography>
                  
                  <Box 
                    sx={{ 
                      // Add styles to customize form fields 
                      '.MuiTextField-root, .MuiAutocomplete-root': {
                        mb: 2.5,
                      },
                      '.MuiInputLabel-root': {
                        fontWeight: 500,
                      },
                      '.MuiInputBase-root': {
                        borderRadius: '8px',
                      },
                      '.MuiButton-root': {
                        borderRadius: '8px',
                        fontWeight: 600,
                        py: 1.5,
                        mt: 2,
                      }
                    }}
                  >
                    <JoinForm />
                  </Box>
                  
                  <Typography variant="body2" sx={{ mt: 3, opacity: 0.7, textAlign: 'center', fontSize: '0.75rem' }}>
                    By submitting this form, you agree to our{' '}
                    <a
                      href="/artist-rights"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#E4FF1A', textDecoration: 'underline' }}
                    >
                      Artist Rights Summary
                    </a>
                    .
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </MetaTagManager>
  );
};

export default JoinPage;